import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";
import { ArrowLeft, Crown, Check, User, Mail, LogOut, CreditCard, Sparkles, AlertCircle, Trash2, Send, Shield, FileText } from "lucide-react";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pollingStatus, setPollingStatus] = useState(null); // null | 'polling' | 'success' | 'failed'
  const [pollAttempts, setPollAttempts] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("annual"); // highlight annual by default
  const [portalLoading, setPortalLoading] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0 = hidden, 1 = confirm
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [digestMsg, setDigestMsg] = useState(null);

  const sessionId = searchParams.get("session_id");
  const isPremium = user?.plan === "premium";

  // Poll payment status when returning from Stripe
  useEffect(() => {
    if (!sessionId || isPremium) return;
    setPollingStatus("polling");
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 8) { setPollingStatus("failed"); return; }
    try {
      const res = await fetch(`${API}/payments/status/${sid}`, { credentials: "include" });
      if (!res.ok) { setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000); return; }
      const data = await res.json();
      if (data.payment_status === "paid") {
        setPollingStatus("success");
        setUser(prev => ({ ...prev, plan: "premium" }));
      } else if (data.status === "expired") {
        setPollingStatus("failed");
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch { setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000); }
    setPollAttempts(attempts + 1);
  };

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, plan_type: selectedPlan }),
      });
      if (!res.ok) throw new Error("Failed to create checkout");
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      alert(t("settings.checkoutFailed"));
    } finally { setCheckoutLoading(false); }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${API}/payments/portal`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) {
        alert(data.detail || t("settings.portalFailed"));
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      alert(t("common.networkError"));
    } finally { setPortalLoading(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSendDigest = async () => {
    setDigestSending(true);
    setDigestMsg(null);
    try {
      const res = await fetch(`${API}/reminders/send-digest`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.status === "sent") {
        setDigestMsg({ type: "success", text: t("settings.digestSent", { email: user.email, count: data.count }) });
      } else if (data.status === "skipped" || data.reason === "email_not_configured") {
        setDigestMsg({ type: "info", text: t("settings.emailNotConfigured") });
      } else if (data.status === "no_upcoming_reminders") {
        setDigestMsg({ type: "info", text: t("settings.noUpcoming") });
      } else {
        setDigestMsg({ type: "error", text: data.error || t("settings.digestFailed") });
      }
    } catch {
      setDigestMsg({ type: "error", text: t("common.networkError") });
    } finally {
      setDigestSending(false);
    }
  };

  const handleSendWeekly = async () => {
    setDigestSending(true);
    setDigestMsg(null);
    try {
      const res = await fetch(`${API}/reminders/send-weekly-summary`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.status === "sent") {
        setDigestMsg({ type: "success", text: t("settings.weeklySent", { email: user.email, count: data.count || 0 }) });
      } else if (data.status === "skipped" || data.reason === "email_not_configured") {
        setDigestMsg({ type: "info", text: t("settings.emailNotConfigured") });
      } else if (data.status === "no_pets") {
        setDigestMsg({ type: "info", text: t("settings.noPetsWeekly") });
      } else {
        setDigestMsg({ type: "error", text: data.error || t("settings.weeklyFailed") });
      }
    } catch {
      setDigestMsg({ type: "error", text: t("common.networkError") });
    } finally {
      setDigestSending(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API}/auth/account`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      setUser(null);
      navigate("/");
    } catch {
      setDeleteLoading(false);
      alert(t("settings.deleteFailed"));
    }
  };

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      <Navigation />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 mb-8 animate-fade-in-up">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-xl hover:bg-white hover:shadow-sm text-slate-500 transition-all">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-extrabold text-slate-800 font-['Nunito']">{t("settings.title")}</h1>
        </div>

        {/* Payment status banner */}
        {pollingStatus === "polling" && (
          <div className="mb-6 bg-[#E3F2FD] rounded-2xl p-4 flex items-center gap-3 animate-fade-in">
            <div className="w-5 h-5 border-2 border-[#1565C0] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium text-[#1565C0]">{t("settings.payProcessing", { cur: pollAttempts, total: 8 })}</p>
          </div>
        )}
        {pollingStatus === "success" && (
          <div className="mb-6 bg-[#E8F5E9] rounded-2xl p-4 flex items-center gap-3 animate-fade-in border border-[#C8E6C9]" data-testid="payment-success-banner">
            <Check size={18} className="text-[#388E3C]" />
            <p className="text-sm font-semibold text-[#388E3C]">{t("settings.paySuccess")}</p>
          </div>
        )}
        {pollingStatus === "failed" && (
          <div className="mb-6 bg-[#FFEBEE] rounded-2xl p-4 flex items-center gap-3 animate-fade-in border border-red-200">
            <AlertCircle size={18} className="text-red-500" />
            <p className="text-sm font-medium text-red-700">{t("settings.payFailed")}</p>
          </div>
        )}

        {/* Profile */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 mb-6 animate-fade-in-up">
          <h2 className="text-base font-bold text-slate-800 font-['Nunito'] mb-5">{t("settings.account")}</h2>
          <div className="flex items-center gap-4 mb-5 pb-5 border-b border-slate-100">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-2xl object-cover border-2 border-[#FF8A65]/20" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-[#FF8A65] flex items-center justify-center text-white font-bold text-xl">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-800">{user?.name}</h3>
                {isPremium && (
                  <span className="text-xs bg-[#FFF5E6] text-[#FF8A65] px-2 py-0.5 rounded-full font-bold flex items-center gap-0.5">
                    <Crown size={9} /> {t("nav.premium")}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <User size={15} className="text-slate-400" />
              <span className="font-medium">{user?.name}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <Mail size={15} className="text-slate-400" />
              <span>{user?.email}</span>
            </div>
          </div>
        </div>

        {/* Plan */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 mb-6 animate-fade-in-up delay-100">
          <h2 className="text-base font-bold text-slate-800 font-['Nunito'] mb-5">{t("settings.subscription")}</h2>
          {isPremium ? (
            <div>
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#FFF9C4]/50 border border-[#FFD54F] mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FFF9C4] flex items-center justify-center">
                  <Crown size={20} className="text-[#FF8A65]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-800">{t("settings.premiumPlan")}</p>
                  <p className="text-sm text-slate-500">
                    {user?.plan_type === "annual" ? t("settings.planYear") : t("settings.planMonth")} · {t("settings.allFeaturesUnlocked")}
                  </p>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {["unlimited", "travel", "sharing", "backup", "export"].map(k => (
                  <li key={k} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Check size={15} className="text-[#66BB6A]" /> {t(`settings.premiumFeatures_${k}`)}
                  </li>
                ))}
              </ul>
              <button
                data-testid="manage-subscription-btn"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="w-full py-3 rounded-full border border-slate-200 bg-white text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <CreditCard size={15} /> {portalLoading ? t("settings.opening") : t("settings.manageSubscription")}
              </button>
              <p className="text-xs text-slate-400 text-center mt-2">{t("settings.manageFootnote")}</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-200 mb-5">
                <div>
                  <p className="font-bold text-slate-800">{t("settings.freePlan")}</p>
                  <p className="text-sm text-slate-500">{t("settings.freePlanSub")}</p>
                </div>
                <span className="text-xs bg-slate-200 text-slate-600 px-3 py-1 rounded-full font-semibold">{t("settings.current")}</span>
              </div>

              {/* Upgrade card */}
              <div className="rounded-2xl border-2 border-[#FF8A65] bg-[#FFF5E6] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={16} className="text-[#FF8A65]" />
                  <p className="font-bold text-slate-800 font-['Nunito']">{t("settings.upgradeTitle")}</p>
                </div>
                <p className="text-sm text-slate-600 mb-4">{t("settings.upgradeSub")}</p>

                {/* Plan selector — monthly vs annual */}
                <div className="grid grid-cols-2 gap-2.5 mb-4">
                  <button
                    type="button"
                    data-testid="plan-monthly-btn"
                    onClick={() => setSelectedPlan("monthly")}
                    className={`relative text-left p-3 rounded-2xl border-2 transition-all ${selectedPlan === "monthly" ? "border-[#FF8A65] bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:border-slate-300"}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">{t("settings.monthly")}</div>
                    <div className="text-xl font-extrabold text-slate-800 font-['Nunito']">$7.99<span className="text-sm font-semibold text-slate-500">/mo</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{t("settings.billedMonthly")}</div>
                  </button>
                  <button
                    type="button"
                    data-testid="plan-annual-btn"
                    onClick={() => setSelectedPlan("annual")}
                    className={`relative text-left p-3 rounded-2xl border-2 transition-all ${selectedPlan === "annual" ? "border-[#FF8A65] bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:border-slate-300"}`}
                  >
                    <div className="absolute -top-2 right-2 text-[10px] font-bold uppercase tracking-wider bg-[#81C784] text-white px-2 py-0.5 rounded-full shadow-sm">{t("settings.save17")}</div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">{t("settings.annual")}</div>
                    <div className="text-xl font-extrabold text-slate-800 font-['Nunito']">$79<span className="text-sm font-semibold text-slate-500">/yr</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{t("settings.approxMonth")}</div>
                  </button>
                </div>

                <ul className="space-y-1.5 mb-5">
                  {["unlimited", "travel", "sharing", "backup"].map(k => (
                    <li key={k} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check size={14} className="text-[#FF8A65]" /> {t(`settings.premiumFeatures_${k}`)}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid="upgrade-btn"
                  onClick={handleUpgrade}
                  disabled={checkoutLoading}
                  className="w-full py-3.5 rounded-full bg-[#FF8A65] text-white font-semibold hover:bg-[#FF7043] transition-all shadow-md disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  <CreditCard size={16} />
                  {checkoutLoading
                    ? t("settings.redirecting")
                    : selectedPlan === "annual"
                      ? t("settings.upgradeAnnual")
                      : t("settings.upgradeMonthly")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Email Reminders */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 mb-6 animate-fade-in-up delay-150">
          <h2 className="text-base font-bold text-slate-800 font-['Nunito'] mb-2">{t("settings.emailReminders")}</h2>
          <p className="text-sm text-slate-500 mb-4">
            {t("settings.emailRemindersBody_daily")}<br/>
            {t("settings.emailRemindersBody_weekly")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              data-testid="send-digest-btn"
              onClick={handleSendDigest}
              disabled={digestSending}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full border-2 border-[#FF8A65] text-[#FF8A65] font-semibold text-sm hover:bg-[#FFF5E6] transition-colors disabled:opacity-60"
            >
              <Send size={14} /> {t("settings.sendDailyDigest")}
            </button>
            <button
              data-testid="send-weekly-btn"
              onClick={handleSendWeekly}
              disabled={digestSending}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors disabled:opacity-60"
            >
              <Sparkles size={14} /> {t("settings.sendWeeklySummary")}
            </button>
          </div>
          {digestMsg && (
            <div
              data-testid="digest-msg"
              className={`mt-3 text-sm rounded-xl p-3 ${
                digestMsg.type === "success" ? "bg-[#E8F5E9] text-[#388E3C]" :
                digestMsg.type === "error" ? "bg-[#FFEBEE] text-red-700" :
                "bg-[#E3F2FD] text-[#1565C0]"
              }`}
            >
              {digestMsg.text}
            </div>
          )}
        </div>

        {/* Legal */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mb-6 animate-fade-in-up delay-200">
          <h2 className="text-base font-bold text-slate-800 font-['Nunito'] mb-4">{t("settings.legal")}</h2>
          <div className="space-y-2">
            <a href="/privacy" target="_blank" rel="noreferrer" data-testid="settings-privacy-link" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
              <Shield size={16} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{t("settings.privacyPolicy")}</span>
            </a>
            <a href="/terms" target="_blank" rel="noreferrer" data-testid="settings-terms-link" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
              <FileText size={16} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{t("settings.termsOfService")}</span>
            </a>
          </div>
        </div>

        {/* Sign out */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mb-6 animate-fade-in-up delay-200">
          <button
            data-testid="settings-logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full border-2 border-red-200 text-red-500 font-semibold hover:bg-red-50 transition-colors"
          >
            <LogOut size={16} /> {t("settings.signOut")}
          </button>
        </div>

        {/* Danger zone */}
        <div className="bg-[#FFF8F8] rounded-3xl border border-red-100 p-6 animate-fade-in-up delay-300">
          <h2 className="text-base font-bold text-red-700 font-['Nunito'] mb-2 flex items-center gap-2">
            <Trash2 size={16} /> {t("settings.dangerZone")}
          </h2>
          <p className="text-sm text-slate-600 mb-4">{t("settings.dangerZoneBody")}</p>
          {deleteStep === 0 ? (
            <button
              data-testid="delete-account-btn"
              onClick={() => setDeleteStep(1)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full border-2 border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors"
            >
              <Trash2 size={15} /> {t("settings.deleteMyAccount")}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl bg-white border border-red-200 p-4 text-sm text-slate-700">
                <p className="font-semibold text-red-700 mb-1">{t("settings.areYouSure")}</p>
                <p><Trans i18nKey="settings.deleteWarning" values={{ email: user?.email }} components={{ b: <strong /> }} /></p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="cancel-delete-btn"
                  onClick={() => setDeleteStep(0)}
                  disabled={deleteLoading}
                  className="flex-1 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
                >
                  {t("common.cancel")}
                </button>
                <button
                  data-testid="confirm-delete-btn"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-60"
                >
                  {deleteLoading ? t("settings.deleting") : t("settings.yesDeleteForever")}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
